import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { useUnsavedChangesPrompt } from '../hooks/useUnsavedChangesPrompt';
import {
  DetailShell, Section, Grid, Icon, FieldText, SubModal, SplitButton,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const LIST_PATH = '/product-segmentation-tree';

// Seçili dalın altına yeni bir alt dal eklemek için satır-içi (inline) giriş.
function InlineAdd({ depth, levelName, value, setValue, onConfirm, onCancel }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '9px 14px', paddingLeft: 14 + depth * 22,
        background: '#EAF1F9', borderBottom: '1px solid var(--dps-line, #E5EBF2)',
      }}
    >
      <span style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--dps-navy, #013E75)', whiteSpace: 'nowrap' }}>
        ↳ {levelName ? `${levelName} ekle` : 'Açıklama'}
      </span>
      <input
        autoFocus
        style={{
          flex: 1, minWidth: 180, maxWidth: 380, height: 36, padding: '0 12px',
          border: '1.5px solid var(--dps-line, #E5EBF2)', borderRadius: 9, background: '#fff',
          fontSize: 13.5, fontWeight: 700, color: 'var(--dps-ink, #0E2A47)', fontFamily: 'inherit', outline: 'none',
        }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onConfirm(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        placeholder="Açıklama"
      />
      <button
        style={{ height: 36, padding: '0 14px', borderRadius: 9, border: 'none', background: 'var(--dps-navy, #013E75)', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}
        onClick={onConfirm}
      >
        Ekle
      </button>
      <button
        style={{ height: 36, padding: '0 14px', borderRadius: 9, border: '1px solid var(--dps-line, #E5EBF2)', background: '#EEF2F7', color: 'var(--dps-ink-2, #48586A)', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}
        onClick={onCancel}
      >
        İptal
      </button>
    </div>
  );
}

function TreeNodeRender({
  node, nodes, levelNames, levelCount,
  expandedNodes, toggleExpand,
  selectedNodeId, onSelect,
  addingUnder, addName, setAddName, onStartAdd, onConfirmAdd, onCancelAdd, onDelete,
  depth = 0,
}) {
  const childNodes = nodes
    .filter((n) => n.parentNodeId === node.id)
    .sort((a, b) => (a.path || a.name || '').localeCompare(b.path || b.name || '', 'tr'));
  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedNodeId === node.id;
  const canAddChild = node.level < levelCount;
  // Dinamik seviye: "yaprak" (en uç, ürün bağlanabilir) = çocuğu olmayan dal. Bir dal
  // en alt seviyeye inmese de leaf olabilir; kayıtlı isLeafNode'a değil çocuk varlığına bakar.
  const isLeaf = childNodes.length === 0;
  const addingHere = addingUnder && addingUnder.parentNodeId === node.id;

  return (
    <div>
      <div
        onClick={() => onSelect(node.id)}
        style={{
          padding: '10px 14px', paddingLeft: 14 + depth * 22,
          borderBottom: '1px solid var(--dps-line, #E5EBF2)',
          borderLeft: `3px solid ${isSelected ? 'var(--dps-azure, #2F7FD1)' : 'transparent'}`,
          fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
          background: isSelected ? '#EAF3FC' : (isLeaf ? 'rgba(14,107,89,0.05)' : 'transparent'),
        }}
      >
        {childNodes.length > 0 ? (
          <button
            onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: 16, color: 'var(--dps-ink-3, #8493A2)', display: 'flex', alignItems: 'center' }}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : <div style={{ width: 16 }} />}
        {node._new && (
          <span title="Kaydedilmedi — 'Kaydet' ile kalıcı olur" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--dps-amber, #C9821B)', flex: 'none' }} />
        )}
        <span
          style={{
            fontSize: 10.5, fontWeight: 800, color: 'var(--dps-navy, #013E75)',
            background: '#EAF1F9', borderRadius: 6, padding: '2px 7px', minWidth: 20, textAlign: 'center',
          }}
          title={`${node.level}. Seviye`}
        >
          {node.level}
        </span>
        <span style={{ fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--dps-ink, #0E2A47)' }}>{node.name}</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--dps-ink-3, #8493A2)', whiteSpace: 'nowrap' }}>
          {levelNames[node.level - 1]}{isLeaf ? ' · Yaprak (en uç)' : ''}
        </span>
        {canAddChild && (
          <button
            onClick={(e) => { e.stopPropagation(); onStartAdd(node.id, node.level + 1); }}
            style={{
              background: '#EAF1F9', border: '1px solid #DBE7F3', color: 'var(--dps-navy, #013E75)',
              borderRadius: 8, padding: '4px 10px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            title={`${node.name} altına ${levelNames[node.level] || 'alt dal'} ekle`}
          >
            ＋ Alt Dal
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(node); }}
          style={{
            background: '#FBECE9', border: '1px solid #F2D6CF', color: 'var(--dps-red, #C0432F)',
            borderRadius: 8, padding: '4px 10px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
          title={`${node.name} dalını sil`}
        >
          Sil
        </button>
      </div>

      {addingHere && (
        <InlineAdd
          depth={depth + 1}
          levelName={levelNames[node.level]}
          value={addName}
          setValue={setAddName}
          onConfirm={() => onConfirmAdd(node.id, node.level + 1)}
          onCancel={onCancelAdd}
        />
      )}

      {isExpanded && childNodes.map((child) => (
        <TreeNodeRender
          key={child.id}
          node={child}
          nodes={nodes}
          levelNames={levelNames}
          levelCount={levelCount}
          expandedNodes={expandedNodes}
          toggleExpand={toggleExpand}
          selectedNodeId={selectedNodeId}
          onSelect={onSelect}
          addingUnder={addingUnder}
          addName={addName}
          setAddName={setAddName}
          onStartAdd={onStartAdd}
          onConfirmAdd={onConfirmAdd}
          onCancelAdd={onCancelAdd}
          onDelete={onDelete}
          depth={depth + 1}
        />
      ))}
    </div>
  );
}

export function ProductSegmentationTreePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isNewMode = !id;
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNewMode ? canAdd : canEdit;

  usePageMeta(
    isNewMode ? 'Yeni Ürün Segmentasyon Ağacı' : 'Ürün Segmentasyon Ağacı',
    isNewMode ? 'Yeni ağaç oluşturun.' : 'Ağacı düzenleyin.'
  );

  const [activeTab, setActiveTab] = useState('parameters');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);

  // Parametre sekme state
  const [treeName, setTreeName] = useState('');
  const [levelNames, setLevelNames] = useState(['']);
  const levelCount = levelNames.length; // Seviye sayısı = tanımlı seviye adedi (dinamik; ayrı input yok).
  const dragLevelIndex = useRef(null);
  const [dragOverLevel, setDragOverLevel] = useState(null);

  // Ağaç sekme state — dallar YEREL tutulur, yalnız "Kaydet"te backend'e yazılır.
  const [nodes, setNodes] = useState([]);            // {id, name, level, parentNodeId, path, isLeafNode, _new?}
  const [deletedNodes, setDeletedNodes] = useState([]); // silinecek KALICI dallar {id, level} (flush'ta)
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [addingUnder, setAddingUnder] = useState(null); // { parentNodeId, level } | null
  const [addName, setAddName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // dal silme onayı için node
  const [deleteTreeConfirm, setDeleteTreeConfirm] = useState(false); // ağaç silme onayı
  const [audit, setAudit] = useState(null); // kayıt bilgisi (oluşturan/güncelleyen + tarihler)
  const tmpCounter = useRef(0);
  // #724 Kaydedilmemiş değişiklik koruması: yüklendiği andaki parametre değerleri (baz).
  // Ağaç adı / seviye adları bunlardan farklıysa veya bekleyen dal varsa "dirty" sayılır.
  const baselineRef = useRef({ name: '', levels: JSON.stringify(['']) });
  const savedBypassRef = useRef(false); // başarılı kayıttan sonra listeye dönerken uyarıyı atla

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // treeName'i DetailShell FieldText'in form/set modeline köprüle.
  const paramForm = { treeName };
  const setParam = (k, v) => { if (k === 'treeName') setTreeName(v); };

  // Load data when editing existing tree
  useEffect(() => {
    if (isNewMode) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/productSegmentationTree/${id}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Ağaç alınamadı');
        const data = await res.json();
        const loadedLevels = data.levelNames && data.levelNames.length ? data.levelNames : [''];
        setTreeName(data.name || '');
        setLevelNames(loadedLevels);
        // #724 Yüklenen değerleri baz al (bunlardan sapma = kaydedilmemiş değişiklik).
        baselineRef.current = { name: data.name || '', levels: JSON.stringify(loadedLevels) };
        setAudit({
          createdBy: data.createdBy ?? data.CreatedBy ?? data.createdUser ?? data.CreatedUser,
          createdDate: data.createdDate ?? data.CreatedDate,
          updatedBy: data.updatedBy ?? data.UpdatedBy ?? data.modifiedUser ?? data.ModifiedUser,
          updatedDate: data.updatedDate ?? data.UpdatedDate ?? data.modifiedDate ?? data.ModifiedDate,
        });

        const nodesRes = await fetch(`/api/productSegmentationTree/${id}/nodes`, { headers: authHeaders() });
        if (nodesRes.ok) setNodes((await nodesRes.json()) || []);
      } catch (e) {
        showToast('error', e.message || 'Ağaç yüklenemedi');
        setTimeout(() => navigate(LIST_PATH), 2000);
      } finally {
        setLoading(false);
      }
    })();
  }, [isNewMode, id, navigate]);

  // Klonla: /new'e clone state ile gelindiyse parametreler + dalları YEREL doldur (hiçbir
  // şey kaydedilmez; kullanıcı "Kaydet" diyene dek yeni ağaç oluşmaz). Dallar temp id'ye remap.
  useEffect(() => {
    if (!isNewMode) return;
    const clone = location.state?.clone;
    if (!clone) return;
    setTreeName(clone.treeName || '');
    setLevelNames(clone.levelNames && clone.levelNames.length ? clone.levelNames : ['']);
    const idMap = {};
    const cloned = (clone.nodes || []).map((n) => {
      const tmpId = `tmp-${(tmpCounter.current += 1)}`;
      idMap[n.id] = tmpId;
      return { ...n, id: tmpId, _new: true };
    });
    cloned.forEach((n) => { if (n.parentNodeId) n.parentNodeId = idMap[n.parentNodeId] || null; });
    setNodes(cloned);
    setTried(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleLevelNameChange = (index, value) => {
    setLevelNames((prev) => prev.map((n, i) => (i === index ? value : n)));
  };
  const addLevel = () => setLevelNames((prev) => (prev.length >= 10 ? prev : [...prev, '']));
  const removeLevel = (index) => setLevelNames((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  // Seviyeleri sürükle-bırak ile sırala (native HTML5 DnD; ekstra kütüphane yok).
  const dropLevel = (toIndex) => {
    const from = dragLevelIndex.current;
    dragLevelIndex.current = null;
    setDragOverLevel(null);
    if (from == null || from === toIndex) return;
    setLevelNames((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const toggleExpand = (nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  };

  // Bir dalın tüm alt ağacını (kendisi dahil) yerel listeden toplar.
  const collectSubtree = (rootId, list) => {
    const acc = [];
    const walk = (pid) => list.filter((n) => n.parentNodeId === pid).forEach((c) => { acc.push(c); walk(c.id); });
    const root = list.find((n) => n.id === rootId);
    if (root) { acc.push(root); walk(rootId); }
    return acc;
  };

  // Kök dal (parentNodeId=null, level=1) ya da bir dalın altına satır-içi giriş kutusunu açar.
  const startAdd = (parentNodeId, level) => {
    setAddName('');
    setAddingUnder({ parentNodeId, level });
    if (parentNodeId) setExpandedNodes((prev) => new Set(prev).add(parentNodeId));
  };
  const cancelAdd = () => { setAddingUnder(null); setAddName(''); };

  // Dal ekleme YEREL — backend'e yazılmaz; "Kaydet"te flush edilir.
  const confirmAdd = (parentNodeId, level) => {
    if (!addName.trim()) { showToast('error', 'Açıklama gerekli'); return; }
    const parent = parentNodeId ? nodes.find((n) => n.id === parentNodeId) : null;
    const path = parent ? `${parent.path || parent.name} > ${addName.trim()}` : addName.trim();
    const tmpId = `tmp-${(tmpCounter.current += 1)}`;
    const added = {
      id: tmpId, name: addName.trim(), level, parentNodeId: parentNodeId || null,
      path, isLeafNode: true, _new: true, // yeni dal çocuksuz → yaprak (leaf display çocuk sayısından hesaplanır)
    };
    setNodes((prev) => [...prev, added]);
    setSelectedNodeId(tmpId);
    if (parentNodeId) setExpandedNodes((prev) => new Set(prev).add(parentNodeId));
    setAddingUnder(null);
    setAddName('');
  };

  // Dal silme YEREL — alt ağacı da kaldırır; kalıcı olanlar flush'ta backend'den silinir.
  const removeNodeLocal = (node) => {
    const subtree = collectSubtree(node.id, nodes);
    const ids = new Set(subtree.map((n) => n.id));
    const persisted = subtree.filter((n) => !n._new).map((n) => ({ id: n.id, level: n.level }));
    setNodes((prev) => prev.filter((n) => !ids.has(n.id)));
    if (persisted.length) setDeletedNodes((prev) => [...prev, ...persisted]);
    if (ids.has(selectedNodeId)) setSelectedNodeId(null);
    if (addingUnder && ids.has(addingUnder.parentNodeId)) cancelAdd();
    setDeleteConfirm(null);
  };

  const reloadNodes = async (treeId) => {
    const res = await fetch(`/api/productSegmentationTree/${treeId}/nodes`, { headers: authHeaders() });
    if (res.ok) setNodes((await res.json()) || []);
  };

  // "Kaydet": parametreleri + tüm bekleyen dal ekleme/silmelerini backend'e yazar.
  // andNew=true → kaydet, listeye/detaya gitme; formu sıfırlayıp yeni ağaca hazırlan.
  const handleSave = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!treeName.trim()) { showToast('error', 'Açıklama gerekli'); setActiveTab('parameters'); return; }
    if (levelNames.some((n) => !n.trim())) { showToast('error', 'Tüm seviyelere isim verin'); setActiveTab('parameters'); return; }

    setSubmitting(true);
    const jsonHeaders = { 'Content-Type': 'application/json', ...authHeaders() };
    try {
      // 1) Ağaç kaydı (yeni=POST / mevcut=PUT)
      let treeId = id;
      if (isNewMode) {
        const res = await fetch('/api/productSegmentationTree', {
          method: 'POST', headers: jsonHeaders,
          body: JSON.stringify({ name: treeName, levelCount, levelNames }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || 'Ağaç oluşturulamadı');
        treeId = body.id ?? body.Id;
      } else {
        const res = await fetch(`/api/productSegmentationTree/${id}`, {
          method: 'PUT', headers: jsonHeaders,
          body: JSON.stringify({ name: treeName, levelNames }),
        });
        if (!res.ok) { const b = await res.json().catch(() => null); throw new Error(b?.message || 'Ağaç güncellenemedi'); }
      }

      // 2) Silmeler — en derin dal önce (backend alt-düğümü olanı silmez).
      const dels = [...deletedNodes].sort((a, b) => b.level - a.level);
      for (const d of dels) {
        const res = await fetch(`/api/productSegmentationTree/${treeId}/nodes/${d.id}`, { method: 'DELETE', headers: authHeaders() });
        if (!res.ok && res.status !== 404) { const b = await res.json().catch(() => null); throw new Error(b?.message || 'Dal silinemedi'); }
      }

      // 3) Eklemeler — üst seviye önce; temp id → gerçek id eşlemesi ile.
      const idMap = {};
      const newNodes = nodes.filter((n) => n._new).sort((a, b) => a.level - b.level);
      for (const nd of newNodes) {
        let parentReal = nd.parentNodeId;
        if (parentReal && idMap[parentReal]) parentReal = idMap[parentReal];
        const res = await fetch(`/api/productSegmentationTree/${treeId}/nodes`, {
          method: 'POST', headers: jsonHeaders,
          body: JSON.stringify({ name: nd.name, level: nd.level, parentNodeId: parentReal || null }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || `"${nd.name}" dalı eklenemedi`);
        idMap[nd.id] = body.id ?? body.Id;
      }

      setDeletedNodes([]);
      if (isNewMode && andNew) {
        // "Kaydet ve Yeni": ağaç oluşturuldu; formu sıfırlayıp yeni ağaca hazırlan (listeye dönme).
        showToast('success', 'Ağaç oluşturuldu. Yeni ağaca devam edebilirsiniz.');
        setTreeName(''); setLevelNames(['']);
        setNodes([]); setSelectedNodeId(null); setAddingUnder(null); setAddName('');
        setExpandedNodes(new Set()); setTried(false);
        setActiveTab('parameters');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      showToast('success', isNewMode ? 'Ağaç oluşturuldu' : 'Değişiklikler kaydedildi');
      // Kaydet sonrası listeye dön (Back Office detay standardı). #724: kayıt başarılı,
      // artık kaydedilmemiş değişiklik yok → guard'ı atla.
      savedBypassRef.current = true;
      setTimeout(() => navigate(LIST_PATH), 500);
    } catch (e) {
      showToast('error', e.message || 'Kaydetme başarısız oldu');
      // Kısmi yazma olmuş olabilir; edit modunda gerçek durumu geri yükle.
      if (!isNewMode) { try { await reloadNodes(id); setDeletedNodes([]); } catch { /* yut */ } }
    } finally {
      setSubmitting(false);
    }
  };

  // Klonla: mevcut ağacın parametre + dallarını taşıyarak yeni-kayıt ekranını açar.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene dek yeni ağaç oluşmaz.
  const handleClone = () => {
    savedBypassRef.current = true; // #724 klonlama bilinçli aksiyon; uyarı çıkarma
    navigate(`${LIST_PATH}/new`, {
      state: {
        clone: {
          treeName: treeName ? `${treeName} (Kopya)` : '',
          levelNames: [...levelNames],
          nodes: nodes.map((n) => ({ ...n })),
        },
      },
    });
  };

  const handleDeleteTree = async () => {
    if (isNewMode) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/productSegmentationTree/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) { const b = await res.json().catch(() => null); throw new Error(b?.message || 'Ağaç silinemedi'); }
      showToast('success', 'Ağaç silindi');
      savedBypassRef.current = true; // #724 silme sonrası guard'ı atla
      setTimeout(() => navigate(LIST_PATH), 600);
    } catch (e) {
      showToast('error', e.message || 'Silme başarısız oldu');
      setSubmitting(false);
      setDeleteTreeConfirm(false);
    }
  };

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) || null;
  const canAddUnderSelected = !!selectedNode && selectedNode.level < levelCount;
  const pendingCount = nodes.filter((n) => n._new).length + deletedNodes.length;
  const deleteConfirmChildCount = deleteConfirm ? collectSubtree(deleteConfirm.id, nodes).length - 1 : 0;

  // #724 Kaydedilmemiş değişiklik: bekleyen dal (ekle/sil) VEYA ağaç adı/seviye adları baz'dan sapmış.
  const isDirty = pendingCount > 0
    || treeName !== baselineRef.current.name
    || JSON.stringify(levelNames) !== baselineRef.current.levels;
  useUnsavedChangesPrompt(
    isDirty,
    'Kaydedilmemiş değişiklikler var. Bu sayfadan çıkarsanız değişiklikler kaybedilecek. Emin misiniz?',
    savedBypassRef,
  );

  const actions = (
    <>
      {!isNewMode && canDelete && (
        <button className="dps-btn danger" onClick={() => setDeleteTreeConfirm(true)} disabled={submitting}>
          <Icon name="x" size={16} /> Sil
        </button>
      )}
      <button className="dps-btn ghost" onClick={() => navigate(LIST_PATH)} disabled={submitting}>İptal</button>
      {canSave && (isNewMode ? (
        <SplitButton
          label={submitting ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={() => handleSave()}
          disabled={submitting}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni ağaç aç',
            icon: 'plus',
            onClick: () => handleSave({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={submitting ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={() => handleSave()}
          disabled={submitting}
          menu={[{
            label: 'Klonla',
            desc: 'Bu ağacı kopyalayıp yeni ağaç aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[
        { label: 'Ürün Segmentasyon Ağaçları', to: LIST_PATH },
        { label: isNewMode ? 'Yeni Ağaç' : 'Ağaç Detayı', current: true },
      ]}
      tabs={[
        { key: 'parameters', label: 'Parametreler', icon: 'list' },
        { key: 'tree', label: 'Ağaç Yapısı', icon: 'layers' },
      ]}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNewMode ? null : audit}
      history={isNewMode ? null : { entityName: 'ProductSegmentationTree', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        {/* ── Parametreler ── */}
        {activeTab === 'parameters' && (
          <>
            <Section icon="info" title="Ağaç Bilgileri" subtitle="Ağaç açıklaması">
              <Grid cols={2}>
                <FieldText form={paramForm} set={setParam} tried={tried} k="treeName" label="Açıklama" req span placeholder="Açıklama girin" />
              </Grid>
            </Section>

            <Section
              icon="layers"
              title="Seviyeler"
              subtitle="Seviye ekleyip adlandırın; sürükleyerek sıralayın (üstten alta: kök → yaprak)"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {levelNames.map((name, idx) => {
                  const invalid = tried && !String(name).trim();
                  const isOver = dragOverLevel === idx;
                  return (
                    <div
                      key={idx}
                      onDragOver={(e) => { e.preventDefault(); if (dragOverLevel !== idx) setDragOverLevel(idx); }}
                      onDrop={() => dropLevel(idx)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 12px', borderRadius: 12,
                        border: `1.5px solid ${isOver ? 'var(--dps-azure, #2F7FD1)' : (invalid ? 'var(--dps-red, #C0432F)' : 'var(--dps-line, #E5EBF2)')}`,
                        background: isOver ? '#EAF3FC' : (invalid ? '#FDF2EF' : '#F6F8FB'),
                        transition: 'background .12s, border-color .12s',
                      }}
                    >
                      <span
                        draggable
                        onDragStart={() => { dragLevelIndex.current = idx; }}
                        onDragEnd={() => { dragLevelIndex.current = null; setDragOverLevel(null); }}
                        title="Sürükleyerek sırala"
                        style={{ cursor: 'grab', color: 'var(--dps-ink-3, #8493A2)', fontSize: 18, lineHeight: 1, userSelect: 'none', padding: '0 2px' }}
                      >
                        ⠿
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 800, color: 'var(--dps-navy, #013E75)',
                        background: '#EAF1F9', borderRadius: 7, padding: '4px 9px', minWidth: 26, textAlign: 'center', flex: 'none',
                      }}>
                        {idx + 1}
                      </span>
                      <input
                        value={name}
                        onChange={(e) => handleLevelNameChange(idx, e.target.value)}
                        placeholder={`Örn: ${['Üretici', 'Marka', 'Alt Marka', 'Ürün Sınıfı'][idx] || `Seviye ${idx + 1}`}`}
                        style={{
                          flex: 1, minWidth: 0, height: 42, padding: '0 14px',
                          border: '1.5px solid var(--dps-line, #E5EBF2)', borderRadius: 10, background: '#fff',
                          fontSize: 14, fontWeight: 700, color: 'var(--dps-ink, #0E2A47)', fontFamily: 'inherit', outline: 'none',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeLevel(idx)}
                        disabled={levelNames.length <= 1}
                        title={levelNames.length <= 1 ? 'En az bir seviye olmalı' : 'Seviyeyi sil'}
                        style={{
                          flex: 'none', width: 38, height: 38, borderRadius: 10,
                          cursor: levelNames.length <= 1 ? 'not-allowed' : 'pointer',
                          border: '1px solid #F2D6CF', background: '#FBECE9', color: 'var(--dps-red, #C0432F)',
                          fontSize: 16, fontWeight: 800, opacity: levelNames.length <= 1 ? 0.5 : 1,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                className="dps-blk-add"
                style={{ marginTop: 12, ...(levelNames.length >= 10 ? { opacity: 0.5, cursor: 'not-allowed' } : null) }}
                onClick={addLevel}
                disabled={levelNames.length >= 10}
              >
                ＋ Seviye Ekle
              </button>

              {tried && levelNames.some((n) => !String(n).trim()) && (
                <div className="dps-ff-msg" style={{ marginTop: 8 }}>Tüm seviyelere isim verin.</div>
              )}
            </Section>
          </>
        )}

        {/* ── Ağaç Yapısı ── */}
        {activeTab === 'tree' && (
          <Section
            icon="layers"
            title="Ağaç Yapısı"
            subtitle={`Bir dal seçip "＋ Alt Dal" ile altına yeni seviye ekleyin · ${levelCount} seviyeli`}
          >
            <div className="dps-note">
              <Icon name="info" size={16} />
              Seviye dinamiktir: bir dalı alt seviyeye indirmek zorunda değilsiniz — <b>çocuğu olmayan her dal “en uç”tur</b> ve Ürün kartından ona bağlanabilir. Eklediğiniz / sildiğiniz dallar <b>“Kaydet”</b>e basana dek kaydedilmez.
              {pendingCount > 0 && <span> · {pendingCount} bekleyen değişiklik</span>}
            </div>

            {/* Araç çubuğu: kök dal ekle + seçili dalın altına alt dal ekle */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
              <button className="dps-blk-add" onClick={() => startAdd(null, 1)}>＋ Yeni Kök Dal</button>
              <button
                className="dps-blk-add"
                style={canAddUnderSelected ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
                onClick={() => { if (canAddUnderSelected) startAdd(selectedNode.id, selectedNode.level + 1); }}
                disabled={!canAddUnderSelected}
                title={selectedNode ? (canAddUnderSelected ? `${selectedNode.name} altına ekle` : 'Bu dal en alt seviyede (yaprak)') : 'Önce bir dal seçin'}
              >
                ＋ Alt Dal Ekle
              </button>
              {selectedNode && (
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--dps-ink-3, #8493A2)' }}>
                  Seçili: <b style={{ color: 'var(--dps-ink, #0E2A47)' }}>{selectedNode.name}</b> ({levelNames[selectedNode.level - 1]})
                </span>
              )}
            </div>

            {/* Kök seviyeye satır-içi ekleme kutusu */}
            {addingUnder && addingUnder.parentNodeId === null && (
              <div style={{ border: '1px solid var(--dps-line, #E5EBF2)', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
                <InlineAdd
                  depth={0}
                  levelName={levelNames[0]}
                  value={addName}
                  setValue={setAddName}
                  onConfirm={() => confirmAdd(null, 1)}
                  onCancel={cancelAdd}
                />
              </div>
            )}

            {nodes.length === 0 ? (
              !(addingUnder && addingUnder.parentNodeId === null) && (
                <div style={{ background: '#F6F8FB', border: '1px dashed var(--dps-line, #E5EBF2)', borderRadius: 12, padding: 28, textAlign: 'center', color: 'var(--dps-ink-3, #8493A2)', fontSize: 13.5, fontWeight: 600 }}>
                  Henüz dal yok. <b style={{ color: 'var(--dps-navy, #013E75)' }}>＋ Yeni Kök Dal</b> ile başlayın.
                </div>
              )
            ) : (
              <div style={{ background: 'var(--dps-card, #fff)', borderRadius: 12, border: '1px solid var(--dps-line, #E5EBF2)', overflow: 'hidden' }}>
                {nodes
                  .filter((n) => !n.parentNodeId)
                  .sort((a, b) => (a.path || a.name || '').localeCompare(b.path || b.name || '', 'tr'))
                  .map((rootNode) => (
                    <TreeNodeRender
                      key={rootNode.id}
                      node={rootNode}
                      nodes={nodes}
                      levelNames={levelNames}
                      levelCount={levelCount}
                      expandedNodes={expandedNodes}
                      toggleExpand={toggleExpand}
                      selectedNodeId={selectedNodeId}
                      onSelect={setSelectedNodeId}
                      addingUnder={addingUnder}
                      addName={addName}
                      setAddName={setAddName}
                      onStartAdd={startAdd}
                      onConfirmAdd={confirmAdd}
                      onCancelAdd={cancelAdd}
                      onDelete={setDeleteConfirm}
                    />
                  ))}
              </div>
            )}
          </Section>
        )}
      </div>

      {/* Dal silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Dal Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(null)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(null)}>İptal</button>
              <button className="dps-btn danger" onClick={() => removeNodeLocal(deleteConfirm)}>Evet, Sil</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{deleteConfirm.name}”</strong> dalını silmek istediğinize emin misiniz?
            {deleteConfirmChildCount > 0 && (
              <> Altındaki <strong>{deleteConfirmChildCount}</strong> alt dal da silinecek.</>
            )}
            <br />
            <span style={{ fontSize: 12.5, color: 'var(--dps-ink-3, #8493A2)' }}>Silme, “Kaydet”e bastığınızda kalıcı olur.</span>
          </p>
        </SubModal>
      )}

      {/* Ağaç silme onayı */}
      {deleteTreeConfirm && (
        <SubModal
          title="Ağaç Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteTreeConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteTreeConfirm(false)} disabled={submitting}>İptal</button>
              <button className="dps-btn danger" onClick={handleDeleteTree} disabled={submitting}>{submitting ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{treeName || 'Bu ağaç'}”</strong> segmentasyon ağacını ve tüm dallarını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default ProductSegmentationTreePage;
